import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Code as CodeIcon,
  Storage as StorageIcon,
  Visibility as VisibilityIcon,
  CheckCircleOutline as ApproveIcon,
  PlayArrow as PlayArrowIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../../store/Index";
import type { EndpointRequest, Priority, HttpMethod } from "../types";
import { notifyRequestCompleted, getUnreadCount } from "../utils/notifications";
import apiService from "../../../service/Api";
import { updateRequest as updateRequestAction } from "../../../store/slice/EndpointRequestsSlice";
import { isBackendDev } from "../utils/permissions";

// const STATUS_COLORS: Record<RequestStatus, "success" | "warning" | "info" | "error" | "default"> = {
//   completed: "success",
//   in_progress: "info",
//   pending: "warning",
//   blocked: "error",
//   cancelled: "default",
// };

const PRIORITY_COLORS: Record<Priority, "error" | "warning" | "info" | "default"> = {
  urgent: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: "#4caf50",
  POST: "#2196f3",
  PUT: "#ff9800",
  PATCH: "#9c27b0",
  DELETE: "#f44336",
};

interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
      detail?: string;
      [key: string]: unknown;
    };
  };
}

interface BackendDashboardProps {
  requests: EndpointRequest[];
  onRequestUpdate: (id: string, updates: Partial<EndpointRequest>) => void;
  onReloadRequests?: () => Promise<void>;
}

export const BackendDashboard: React.FC<BackendDashboardProps> = ({
  requests,
  onRequestUpdate,
  onReloadRequests,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const [activeTab, setActiveTab] = useState<"requested" | "in_progress">("requested");
  const [searchTerm, setSearchTerm] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<EndpointRequest | null>(null);
  const [endpointUrl, setEndpointUrl] = useState("");
  const [statusCode, setStatusCode] = useState<number | undefined>(undefined);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [stats, setStats] = useState({
    requested: 0,
    inProgress: 0,
    completed: 0,
  });

  // Check if user is backend developer based on permissions
  const userIsBackendDev = isBackendDev(user);

  // Update notification count
  useEffect(() => {
    if (!userIsBackendDev) return;

    const updateCount = () => {
      setNotificationCount(getUnreadCount("backend"));
    };

    updateCount();
    window.addEventListener(
      "endpoint-request-notification-count-updated",
      updateCount
    );

    return () => {
      window.removeEventListener(
        "endpoint-request-notification-count-updated",
        updateCount
      );
    };
  }, [userIsBackendDev]);


  // Load stats from API
  const loadStats = React.useCallback(async () => {
    if (!userIsBackendDev) return;
    
    try {
      const response = await apiService.get<{
        total_requests: number;
        status_counts: {
          completed?: number;
          pending?: number;
          in_progress?: number;
          blocked?: number;
          cancelled?: number;
        };
      }>("/api/endpoint-request/endpoint-requests/statistics/");

      setStats({
        requested: response.data.status_counts.pending || 0,
        inProgress: response.data.status_counts.in_progress || 0,
        completed: response.data.status_counts.completed || 0,
      });
    } catch (error: unknown) {
      console.error("Error loading stats:", error);
      // Fallback to calculating from requests
      setStats({
        requested: requests.filter((r) => r.status === "pending").length,
        inProgress: requests.filter((r) => r.status === "in_progress").length,
        completed: requests.filter((r) => r.status === "completed").length,
      });
    }
  }, [userIsBackendDev, requests]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Reload stats when requests change
  useEffect(() => {
    if (userIsBackendDev && requests.length > 0) {
      loadStats();
    }
  }, [requests.length, userIsBackendDev, loadStats]);

  // Listen for notifications
  useEffect(() => {
    if (!userIsBackendDev) return;

    const handleNotification = (event: CustomEvent) => {
      const notification = event.detail;
      if (notification.type === "request_created") {
        toast.info(`New endpoint request: ${notification.component} - ${notification.task}`, {
          autoClose: 5000,
        });
      }
    };

    window.addEventListener("endpoint-request-notification", handleNotification as EventListener);
    return () => {
      window.removeEventListener("endpoint-request-notification", handleNotification as EventListener);
    };
  }, [userIsBackendDev]);

  // Filter requests based on active tab
  const filteredRequests = useMemo(() => {
    let filtered = [...requests];

    if (activeTab === "requested") {
      filtered = filtered.filter((r) => r.status === "pending");
    } else if (activeTab === "in_progress") {
      filtered = filtered.filter((r) => r.status === "in_progress");
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter((r) => r.priority === priorityFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.createdBy?.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [requests, activeTab, priorityFilter, searchTerm]);

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleStartWork = async (request: EndpointRequest) => {
    if (!request.id) {
      toast.error("Request ID is missing");
      return;
    }

    try {
      // Make API call to update status to in_progress
      await apiService.patch<{
        status: string;
      }>(`/api/endpoint-request/endpoint-requests/${request.id}/`, {
        status: "in_progress",
      });

      // Update Redux store
      dispatch(updateRequestAction({
        id: request.id,
        updates: {
          status: "in_progress",
          updatedAt: new Date().toISOString(),
        },
      }));

      // Also call the parent component's update handler for backward compatibility
      onRequestUpdate(request.id, {
        status: "in_progress",
        updatedAt: new Date().toISOString(),
      });

      // Reload requests from API to ensure all users see the updated status
      if (onReloadRequests) {
        await onReloadRequests();
      }

      toast.success(`Started working on: ${request.component}`);
    } catch (error: unknown) {
      console.error("Error updating request status:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to update request status. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleOpenApproveDialog = (request: EndpointRequest) => {
    setSelectedRequest(request);
    setEndpointUrl(request.endpoint || "");
    setStatusCode(request.statusCode);
    setOpenApproveDialog(true);
  };

  const handleApproveAndComplete = async () => {
    if (!selectedRequest) return;

    if (!selectedRequest.id) {
      toast.error("Request ID is missing");
      return;
    }

    if (!endpointUrl || endpointUrl.trim() === "") {
      toast.error("Please enter the API endpoint URL");
      return;
    }

    try {
      // Make API call to update status to completed with endpoint URL
      await apiService.patch<{
        status: string;
        endpoint_url: string;
      }>(`/api/endpoint-request/endpoint-requests/${selectedRequest.id}/`, {
        status: "completed",
        endpoint_url: endpointUrl.trim(),
      });

      const updates: Partial<EndpointRequest> = {
        status: "completed",
        endpoint: endpointUrl.trim(),
        statusCode: statusCode,
        actualCompletionDate: new Date().toISOString().split("T")[0],
        approvedBy: user?.username || "backend",
        approvedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Update Redux store
      dispatch(updateRequestAction({
        id: selectedRequest.id,
        updates,
      }));

      // Also call the parent component's update handler for backward compatibility
      onRequestUpdate(selectedRequest.id, updates);
      
      // Reload requests from API to ensure all users see the updated status
      if (onReloadRequests) {
        await onReloadRequests();
      }
      
      // Notify frontend and admin users about completed request
      notifyRequestCompleted({
        id: selectedRequest.id,
        component: selectedRequest.component,
        task: selectedRequest.task,
        endpoint: endpointUrl.trim(),
        approvedBy: user?.username || "backend",
        createdBy: selectedRequest.createdBy,
      });
      
      toast.success(`Endpoint request completed: ${selectedRequest.component}`);
      toast.info(`Endpoint URL: ${endpointUrl.trim()} is now available to frontend developers`);
      toast.success(`Frontend and Admin users have been notified`);

      // Reset dialog
      setOpenApproveDialog(false);
      setSelectedRequest(null);
      setEndpointUrl("");
      setStatusCode(undefined);

      // Dispatch event to notify frontend users
      window.dispatchEvent(
        new CustomEvent("endpoint-request-completed", {
          detail: {
            requestId: selectedRequest.id,
            endpoint: endpointUrl.trim(),
            component: selectedRequest.component || "Unknown",
          },
        })
      );
    } catch (error: unknown) {
      console.error("Error completing request:", error);
      const apiError = error as ApiErrorResponse;
      const errorMessage =
        apiError.response?.data?.message ||
        apiError.response?.data?.detail ||
        "Failed to complete request. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
  };

  if (!userIsBackendDev) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}05 100%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <StorageIcon sx={{ fontSize: 40, color: primaryColor }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h4" fontWeight="bold" sx={{ color: "text.primary" }}>
                Backend Developer Dashboard
              </Typography>
              {notificationCount > 0 && (
                <Chip
                  label={notificationCount}
                  color="error"
                  size="small"
                  sx={{ fontWeight: "bold" }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Manage and complete endpoint requests
            </Typography>
          </Box>
        </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "warning.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Requested
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    {stats.requested}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: "warning.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "info.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    In Progress
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="info.main">
                    {stats.inProgress}
                  </Typography>
                </Box>
                <PlayArrowIcon sx={{ fontSize: 40, color: "info.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "success.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {stats.completed}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, boxShadow: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 64,
            },
          }}
        >
          <Tab label={`Requested (${stats.requested})`} value="requested" />
          <Tab label={`In Progress (${stats.inProgress})`} value="in_progress" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Search by component, task, lead, or endpoint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(e.target.value as Priority | "all")}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setPriorityFilter("all");
                setSearchTerm("");
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ boxShadow: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 700 }}>Component</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Requested By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow key={request.id || `request-${request.component}-${request.task}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.component}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={request.task}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {request.task}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.createdBy || request.lead || "Unknown"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {request.endpoint ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CodeIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {request.endpoint}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip label="Not assigned" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>
                      {request.method && (
                        <Chip
                          label={request.method}
                          size="small"
                          sx={{
                            backgroundColor: METHOD_COLORS[request.method as HttpMethod] || "#666",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "0.7rem",
                            height: 22,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.priority.toUpperCase()}
                        color={PRIORITY_COLORS[request.priority]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontSize="0.875rem">
                        {request.requestDate ? new Date(request.requestDate).toLocaleDateString() : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {request.imageUrl ? (
                        <Tooltip title="View Image">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              if (request.imageUrl) {
                                setPreviewImageUrl(request.imageUrl);
                                setImagePreviewOpen(true);
                              }
                            }}
                          >
                            <ImageIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No image
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                        {request.status === "pending" && (
                          <Tooltip title="Start Working">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleStartWork(request)}
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(request.status === "pending" || request.status === "in_progress") && (
                          <Tooltip title="Approve & Complete">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenApproveDialog(request)}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              toast.info(`Viewing request: ${request.component}`);
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Approve & Complete Dialog */}
      <Dialog 
        open={openApproveDialog} 
        onClose={() => {
          setOpenApproveDialog(false);
          setSelectedRequest(null);
          setEndpointUrl("");
          setStatusCode(undefined);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ApproveIcon sx={{ color: "success.main" }} />
            <Typography variant="h6">Approve & Complete Endpoint</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Once you complete this request, the endpoint URL will be visible to frontend developers
                and the request will be marked as accomplished.
              </Alert>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Component
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedRequest.component}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Task
                </Typography>
                <Typography variant="body1">
                  {selectedRequest.task}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="API Endpoint URL *"
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                placeholder="https://yourdomain.com/api/endpoint/path/"
                required
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CodeIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the complete API endpoint URL that frontend developers will use"
              />

              <TextField
                fullWidth
                label="HTTP Status Code (Optional)"
                type="number"
                value={statusCode || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const parsed = parseInt(value, 10);
                    setStatusCode(!isNaN(parsed) ? parsed : undefined);
                  } else {
                    setStatusCode(undefined);
                  }
                }}
                placeholder="200"
                sx={{ mb: 2 }}
                inputProps={{ min: 100, max: 599 }}
                helperText="Enter the HTTP status code (e.g., 200 for success)"
              />

              {/* Image Display */}
              {selectedRequest.imageUrl && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                    Endpoint Application Context
                  </Typography>
                  <Box
                    component="img"
                    src={selectedRequest.imageUrl}
                    alt="Endpoint application context"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      toast.error("Failed to load image");
                    }}
                    sx={{
                      width: "100%",
                      maxHeight: 400,
                      objectFit: "contain",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      if (selectedRequest.imageUrl) {
                        setPreviewImageUrl(selectedRequest.imageUrl);
                        setImagePreviewOpen(true);
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Click image to view full size
                  </Typography>
                </Box>
              )}

              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> After completing this request:
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li>{`The endpoint URL will appear in the frontend developer's dialog`}</li>
                    <li>{`The request will be marked as "completed" and "accomplished"`}</li>
                    <li>Frontend developers will be notified</li>
                  </ul>
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenApproveDialog(false);
              setSelectedRequest(null);
              setEndpointUrl("");
              setStatusCode(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveAndComplete}
            variant="contained"
            color="success"
            disabled={!endpointUrl || endpointUrl.trim() === ""}
            startIcon={<ApproveIcon />}
            sx={{ backgroundColor: primaryColor, "&:hover": { backgroundColor: primaryColor, opacity: 0.9 } }}
          >
            Approve & Mark as Completed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setPreviewImageUrl(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Endpoint Application Context</Typography>
            <IconButton
              onClick={() => {
                setImagePreviewOpen(false);
                setPreviewImageUrl(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="Endpoint application context"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                toast.error("Failed to load image");
              }}
              sx={{
                width: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

